
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    Title
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { MatrixController, MatrixElement } from "chartjs-chart-matrix";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import dayjs from "dayjs";
import ChartHeader from "examples/Charts/General/ChartHeader";
import MetricCardsContainer from "layouts/insight-hub/components/MetricCardsContainer";
import ChartNotEntitled from "examples/Charts/General/ChartNotEntitled";
import ChartAssistant from "components/ChartAssistant"; // ✅ ADDED

import { color } from "chart.js/helpers";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import youtubeLogoDark from "assets/images/logos/youtube-logo-dark.png";
import { generateColors } from "utils/chartColors";

// -----------------------------------------------------------
// Chart.js registration
// -----------------------------------------------------------
ChartJS.register(
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
    Title,
    MatrixController,
    MatrixElement
);

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// =============================
// Plugins
// =============================
const renderVerticalHighlight = {
    id: "renderVerticalHighlight",
    afterDraw: (chart) => {
        if (!chart.tooltip || !chart.tooltip._active || chart.tooltip._active.length === 0) return;

        const activePoint = chart.tooltip._active[0];
        if (!activePoint?.element?.$context?.raw) return;

        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        const scaleX = chart.scales.x;
        if (!scaleX) return;

        // Get value from the hovered point
        const rawX = activePoint.element.$context.raw.x;

        // Determine the integer "Day" index (0 = Mon, 1 = Tue, etc.)
        // Since days are centered at integers and span +/- 0.5
        const dayIndex = Math.round(rawX);

        // Calculate visual boundaries for this day
        const left = scaleX.getPixelForValue(dayIndex - 0.5);
        const right = scaleX.getPixelForValue(dayIndex + 0.5);
        const width = right - left;

        if (width <= 0) return;

        ctx.save();
        ctx.fillStyle = "rgba(0, 0, 0, 0.05)"; // Subtle grey
        ctx.fillRect(left, chartArea.top, width, chartArea.bottom - chartArea.top);
        ctx.restore();
    },
};

// -----------------------------------------------------------
// Helpers
// -----------------------------------------------------------
const getPostEngagement = (post, platform) => {
    if (platform === "YouTube") {
        const vm = post.videoMetrics || {};
        return (vm.likeCount || 0) + (vm.commentCount || 0);
    } else {
        const pm = post.postMetrics || {};
        const reactions =
            (pm.likes || pm.like || 0) +
            (pm.love || 0) +
            (pm.wow || 0) +
            (pm.haha || 0) +
            (pm.sad || 0) +
            (pm.angry || 0);
        return reactions + (pm.comments || 0) + (pm.shares || 0);
    }
};

const getAllPostsWithDates = (profile) => {
    const allPosts = [];
    if (profile.data) {
        profile.data.forEach(dayItem => {
            const date = dayItem.date || dayItem.datetime;
            if (dayItem.posts) {
                dayItem.posts.forEach(p => {
                    const postDate = p.created_time || p.taken_at || p.date || date;
                    allPosts.push({ ...p, date: postDate, platform: profile.platform });
                });
            }
            if (dayItem.videos) {
                dayItem.videos.forEach(v =>
                    allPosts.push({
                        ...v,
                        date: v.publishedAt || v.created_time || date,
                        platform: profile.platform
                    })
                );
            }
        });
    }
    return allPosts;
};

// -----------------------------------------------------------
// Helpers
// -----------------------------------------------------------
// HELPER: Render Platform Icon
const renderPlatformIcon = (platform) => {
    switch (platform) {
        case "Facebook":
            return <FacebookRoundedIcon sx={{ fontSize: 20 }} />;
        case "Instagram":
            return <InstagramIcon sx={{ fontSize: 20 }} />;
        case "YouTube":
            return (
                <MDBox
                    component="img"
                    src={youtubeLogoDark}
                    alt="YouTube"
                    sx={{ width: 16, height: 16, objectFit: "contain" }}
                />
            );
        default:
            return null;
    }
};

// -----------------------------------------------------------
// Component
// -----------------------------------------------------------
function OptimalPostingTimeChart({
    title = "",
    datasets = [],
    previousdatasets = [],
    isLoading = false,
    isEntitled = true,
    featureName = "",
    planRequired = "",
    entitlements, // ✅ Added prop
}) {
    const safeDatasets = Array.isArray(datasets) ? datasets : [];

    // -----------------------------------------------------------
    // Metrics Processing & Colors
    // -----------------------------------------------------------
    const profileIds = useMemo(() => safeDatasets.map(d => d.profileId || d.label), [safeDatasets]);
    const chartColors = useMemo(() => generateColors(safeDatasets.length, profileIds), [safeDatasets, profileIds]);

    // -----------------------------------------------------------
    // Heatmap data
    // -----------------------------------------------------------
    const matrixData = useMemo(() => {
        const dataPoints = [];
        let globalMaxAvg = 0; // Keep track if needed, but we'll use local max for color
        const numProfiles = safeDatasets.length;
        const widthPerProfile = 1 / numProfiles;

        safeDatasets.forEach((profile, index) => {
            const grid = {};
            let profileMaxAvg = 0;

            // Initialize grid for this profile
            for (let d = 1; d <= 7; d++) {
                for (let h = 0; h < 24; h++) {
                    grid[`${d}-${h}`] = { totalEng: 0, count: 0 };
                }
            }

            // Populate grid
            getAllPostsWithDates(profile).forEach(post => {
                const dObj = dayjs(post.date);
                const day = dObj.isoWeekday(); // 1–7
                const hour = dObj.hour();
                const key = `${day}-${hour}`;
                const eng = getPostEngagement(post, profile.platform);

                if (grid[key]) {
                    grid[key].totalEng += eng;
                    grid[key].count += 1;
                }
            });

            // First pass: Find max average for THIS profile
            for (let d = 1; d <= 7; d++) {
                for (let h = 0; h < 24; h++) {
                    const cell = grid[`${d}-${h}`];
                    const avg = cell.count ? cell.totalEng / cell.count : 0;
                    if (avg > profileMaxAvg) profileMaxAvg = avg;
                }
            }

            // Update global max just in case
            if (profileMaxAvg > globalMaxAvg) globalMaxAvg = profileMaxAvg;

            // Second pass: Create data points
            for (let d = 1; d <= 7; d++) {
                for (let h = 0; h < 24; h++) {
                    const cell = grid[`${d}-${h}`];
                    const avg = cell.count ? cell.totalEng / cell.count : 0;

                    const dayIndex = d - 1;
                    const xPos = (dayIndex - 0.5) + (index * widthPerProfile) + (widthPerProfile / 2);

                    dataPoints.push({
                        x: xPos,
                        y: h,
                        v: avg,
                        count: cell.count,
                        baseColor: chartColors[index],
                        profileName: profile.name || profile.label,
                        profileMaxAvg: profileMaxAvg // Store local max
                    });
                }
            }
        });

        return { dataPoints, maxAvg: globalMaxAvg, numProfiles };
    }, [safeDatasets, chartColors]);

    // -----------------------------------------------------------
    // Metrics Processing
    // -----------------------------------------------------------
    const engagementMetrics = useMemo(() => {
        if (!safeDatasets) return [];
        return safeDatasets.map((profile) => {
            if (!profile) return null;

            // Helper to calculate optimal time
            const getOptimalTime = (posts) => {
                const grid = {};
                posts.forEach(p => {
                    const dObj = dayjs(p.date);
                    const key = `${dObj.isoWeekday()}-${dObj.hour()}`;
                    if (!grid[key]) grid[key] = { total: 0, count: 0 };
                    grid[key].total += getPostEngagement(p, profile.platform);
                    grid[key].count++;
                });

                let maxAvg = -1;
                let bestKey = null;

                Object.keys(grid).forEach(key => {
                    const { total, count } = grid[key];
                    const avg = count ? total / count : 0;
                    if (avg > maxAvg) {
                        maxAvg = avg;
                        bestKey = key;
                    }
                });

                if (!bestKey) return "N/A";
                const [d, h] = bestKey.split('-');
                // d is 1-7 (Mon-Sun), DAYS is 0-indexed
                return `${DAYS[d - 1]} @ ${dayjs().hour(h).format("h A")}`;
            };

            // Current Optimal Time
            const currentOptimal = getOptimalTime(getAllPostsWithDates(profile));

            // Previous Optimal Time
            const previousProfile = previousdatasets?.find((p) => p.profileId === profile.profileId);
            let previousOptimal = "N/A";
            if (previousProfile) {
                previousOptimal = getOptimalTime(getAllPostsWithDates(previousProfile));
            }

            return {
                profilePic: profile.profilePictureUrl || "https://via.placeholder.com/150",
                name: profile.name || profile.label || "Unknown",
                platform: profile.platform,
                metricName: "Optimal Posting Time",
                metricValue: currentOptimal,
                previousMetricValue: previousOptimal,
                isQualitative: true // 🚩 Tell MetricCard not to show percentage change
            };
        }).filter(Boolean);
    }, [safeDatasets, previousdatasets]);

    // -----------------------------------------------------------
    // Chart Data
    // -----------------------------------------------------------
    const chartData = {
        datasets: [
            {
                label: "Engagement",
                data: matrixData.dataPoints,
                backgroundColor(ctx) {
                    const value = ctx.raw?.v || 0;
                    if (value === 0) return "#ffffff";

                    const base = ctx.raw?.baseColor || "#ff5722";
                    // Use local profile max average if available, otherwise fallback to global max
                    const maxVal = ctx.raw?.profileMaxAvg || matrixData.maxAvg || 1;

                    const alpha = Math.max(value / maxVal, 0.1);
                    return color(base).alpha(alpha).rgbString();
                },
                borderColor: "rgba(0,0,0,0.1)",
                borderWidth: 1,

                // Dynamic width based on profiles
                width: ({ chart }) => {
                    if (!chart.chartArea) return 0;
                    // Divide total column width by number of profiles
                    const colWidth = chart.chartArea.width / 7;
                    return (colWidth / matrixData.numProfiles) - 1; // -1 for padding
                },
                height: ({ chart }) => {
                    if (!chart.chartArea) return 0;
                    return chart.chartArea.height / 24 - 1;
                },
            }
        ]
    };


    // -----------------------------------------------------------
    // Chart Options
    // -----------------------------------------------------------
    const options = {
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: () => "",
                    label: ({ raw }) => [
                        `${raw.profileName}`,
                        // Map back approximate X to day label for clarity
                        `${DAYS[Math.round(raw.x)]} @ ${dayjs().hour(raw.y).format("h A")}`,
                        `Avg Engagement: ${raw.v.toFixed(1)}`,
                        `Posts: ${raw.count}`,
                    ]
                }
            }
        },
        scales: {
            x: {
                type: "linear",
                min: -0.5,
                max: 6.5,
                offset: false,
                ticks: {
                    stepSize: 1,
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0,
                    align: 'center',
                    callback: (val) => {
                        // Only label integer ticks (the daily centers)
                        if (val % 1 === 0) {
                            return DAYS[val] || "";
                        }
                        return "";
                    },
                    color: "#7b809a",
                    font: { size: 12 }
                },
                afterBuildTicks: (axis) => {
                    axis.ticks = [0, 1, 2, 3, 4, 5, 6].map(v => ({ value: v }));
                },
                grid: { display: false }
            },
            y: {
                type: "linear",
                min: -0.5,
                max: 23.5,
                reverse: true,
                title: {
                    display: true,
                    text: 'Time of Day',
                    color: '#999',
                    font: {
                        size: 12,
                        weight: 'bold',
                        style: 'normal'
                    },
                    padding: { bottom: 0 }
                },
                ticks: {
                    stepSize: 1,
                    callback: v => dayjs().hour(v).format("h A"),
                    color: "#7b809a",
                    font: { size: 10 }
                },
                grid: { display: false }
            }
        },
        onHover: (event, chartElements, chart) => {
            chart.canvas.style.cursor = chartElements.length ? 'pointer' : 'default';
        }
    };


    // ⬇️=========================================
    // ⬇️ ADDED: Text Context for AI
    // ⬇️=========================================
    const aiContext = useMemo(() => {
        let text = `Chart Description: A heatmap of user engagement by day and hour. The best times to post are highlighted.\n\nInsights:`;

        if (engagementMetrics && engagementMetrics.length > 0) {
            engagementMetrics.forEach(metric => {
                text += `\n\nProfile: ${metric.name} (${metric.platform})`;
                text += `\nCalculated Best Time: ${metric.metricValue}`;
            });
        } else {
            text += "\nNo sufficient data to calculate optimal times.";
        }
        return text;
    }, [engagementMetrics]);

    // ⬇️=========================================
    // ⬇️ ADDED: Synthetic Chart Data for AI (Top 5 List)
    // ⬇️=========================================
    const aiChartData = useMemo(() => {
        if (!safeDatasets || safeDatasets.length === 0) return null;

        const aiDatasets = safeDatasets.map(profile => {
            // 1. Calculate Grid
            const grid = [];
            getAllPostsWithDates(profile).forEach(post => {
                const dObj = dayjs(post.date);
                const day = dObj.isoWeekday(); // 1-7
                const hour = dObj.hour(); // 0-23
                const key = `${day}-${hour}`;
                const eng = getPostEngagement(post, profile.platform);

                // Find existing entry or push new
                let cell = grid.find(c => c.key === key);
                if (!cell) {
                    cell = { key, day, hour, total: 0, count: 0 };
                    grid.push(cell);
                }
                cell.total += eng;
                cell.count++;
            });

            // 2. Calculate Avg & Sort Top 5
            const top5 = grid.map(c => ({
                label: `${DAYS[c.day - 1]} ${dayjs().hour(c.hour).format("hA")}`,
                value: c.count ? (c.total / c.count) : 0
            }))
                .sort((a, b) => b.value - a.value)
                .slice(0, 5);

            // 3. Shape for AI (Labels = Times, Data = Engagement)
            return {
                label: `${profile.name || profile.label} Top Times`,
                data: top5, // Sending object array directly, or we can split
                // Better to split for standard Chart.js shape if AI expects that
                // But let's send a simplified object list since our new AI prompt handles it well
                topTimes: top5.map(t => `${t.label} (Score: ${t.value.toFixed(1)})`)
            };
        });

        // HACK: Reformat to standard Chart.js structure "enough" for the AI validator
        // Labels = Aggregated names of top times, Data = Scores
        // But since times differ per profile, this is messy. 
        // Best approach: Send a "List" dataset where data is just an array of numbers, and labels are generic 1-5
        // AND include the text details in the "label" string or a special prop.
        // Actually, the AI Fix I made for Word Cloud uses "labels" and "datasets[].data".
        // Let's coerce Top 5 into 5 bars.
        const labels = ["1st Best", "2nd Best", "3rd Best", "4th Best", "5th Best"];
        const datasets = aiDatasets.map(d => ({
            label: d.label,
            data: d.topTimes || [], // Passing string array? No, strict Chart.js wants numbers.
            // Let's pass the SCORES as data, and the TIME STRINGS as "extra info" if possible.
            // But 'ChartAssistant' strips everything except numbers.
            // So... The AI *needs* the Time Strings to answer "When?".
            // The Context String (aiContext) HAS the time strings.
            // So for the "Chart Data", I just need to show the AI that there is DATA (Peaks).
            data: d.data.map(item => item.value)
        }));

        return {
            labels,
            datasets
        };
    }, [safeDatasets]);

    // -----------------------------------------------------------
    // Render
    // -----------------------------------------------------------
    const hasData = safeDatasets.length > 0;

    const tooltipContent = (
        <div style={{ maxWidth: "350px", textAlign: "left" }}>
            <Typography variant="body2" gutterBottom>
                <strong>Description:</strong>
            </Typography>
            <Typography variant="caption" gutterBottom display="block">
                A heatmap showing when your audience is most active and responsive.
                Darker cells indicate higher average engagement.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>How to read the chart:</strong>
            </Typography>
            <Typography variant="caption" gutterBottom display="block">
                - <strong>Rows (Y)</strong>: Time of day (AM/PM).<br />
                - <strong>Columns (X)</strong>: Day of the week.<br />
                - <strong>Color Intensity</strong>: Darker = Higher Engagement.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>Insights you can gain:</strong>
            </Typography>
            <Typography variant="caption" display="block">
                - Pinpoint the exact hour to schedule your posts for maximum impact. <br />
                - Identify "dead zones" where engagement is consistently low. <br />
                - Patterns may reveal your audience's daily routine (e.g., active during commute).
            </Typography>
        </div>
    );

    return (
        <Card sx={{ display: "flex", flexDirection: "column" }}>
            <ChartHeader
                title={title || "Optimal Posting Time"}
                description={tooltipContent}
                profilePictures={engagementMetrics.map(m => m.profilePic)}
                is_derived
            >
                {/* ✅ AI Assistant */}
                <ChartAssistant
                    title={title || "Optimal Posting Time"}
                    context={aiContext}
                    chartData={aiChartData} // Synthetic Top 5
                    rawData={safeDatasets}
                    sx={{
                        position: "static",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        ml: 2
                    }}
                    entitlements={entitlements} // ✅ Pass entitlements
                />
            </ChartHeader>

            <div style={{ position: "relative", paddingBottom: "1rem" }}>
                {!isEntitled && (
                    <ChartNotEntitled
                        featureName={featureName}
                        planRequired={planRequired}
                    />
                )}

                <MetricCardsContainer metrics={engagementMetrics} />

                {/* --- Custom Legend --- */}
                <MDBox display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" gap={3} mt={1} mb={2}>
                    {safeDatasets.map((profile, index) => (
                        <MDBox key={index} display="flex" alignItems="center" gap={1}>
                            {/* Color Box */}
                            <MDBox
                                sx={{
                                    width: 30,
                                    height: 12,
                                    backgroundColor: chartColors[index],
                                    borderRadius: "2px",
                                }}
                            />
                            {/* Platform Icon and Name */}
                            <MDBox display="flex" alignItems="center" gap={0.5} sx={{ color: "#344767" }}>
                                {renderPlatformIcon(profile.platform)}
                                <MDTypography variant="caption" fontWeight="medium" color="text">
                                    {profile.name || profile.label}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    ))}
                </MDBox>

                <MDBox p={2} pt={0} sx={{ position: "relative", height: 400 }}>
                    {isLoading && (
                        <MDBox
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                                position: "absolute",
                                inset: 0,
                                backgroundColor: "rgba(255,255,255,0.7)",
                                zIndex: 10
                            }}
                        >
                            <CircularProgress size={32} thickness={4} color="accent" />
                        </MDBox>
                    )}

                    {hasData ? (
                        <Chart
                            type="matrix"
                            data={chartData}
                            options={options}
                            plugins={[renderVerticalHighlight]} // ✅ Register Plugin
                        />
                    ) : (
                        <MDTypography variant="body2" align="center">
                            No data available for heatmap.
                        </MDTypography>
                    )}
                </MDBox>
            </div>

            {/* ✅ Disclaimer Footer */}
            {isEntitled && (
                <MDBox p={1} display="flex" justifyContent="flex-end">
                    <MDTypography
                        variant="caption"
                        color="text"
                        sx={{
                            fontStyle: "italic",
                            fontSize: "0.7rem",
                            opacity: 0.7,
                            mr: 1
                        }}
                    >
                        *Metric calculated by Comparedly
                    </MDTypography>
                </MDBox>
            )}
        </Card>
    );
}

OptimalPostingTimeChart.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    datasets: PropTypes.array,
    previousdatasets: PropTypes.array,
    isLoading: PropTypes.bool,
    isEntitled: PropTypes.bool,
    featureName: PropTypes.string,
    planRequired: PropTypes.string,
    entitlements: PropTypes.object,
};

export default OptimalPostingTimeChart;
